import {
  DragEndEvent,
  DragStartEvent,
  PointerSensor,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import {
  ColumnDef,
  ExpandedState,
  InitialTableState,
  PaginationState,
  Row,
  RowSelectionState,
  Updater,
} from '@tanstack/react-table';
import { isEmpty } from 'lodash-es';
import { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useSingleTableShortcutSelection } from 'src/components/Events/EventPage/hooks/useSingleTableShortcutSelection';
import { useExpandCollapseListings } from 'src/components/Listings/InventoryEventPage/InventoryEventPage.hooks';
import { useActivePosEntityContext } from 'src/contexts/ActivePosEntityContext';
import { useActiveSortTableColumnContext } from 'src/contexts/ActiveSortTableColumnContext';
import { useAppContext } from 'src/contexts/AppContext';
import { useCatalogDataContext } from 'src/contexts/CatalogDataContext';
import { useClientLogContext } from 'src/contexts/ClientLogContext';
import { FocusContext } from 'src/contexts/InputPriceFocusContext/FocusContext.types';
import { useInputPriceFocusContext } from 'src/contexts/InputPriceFocusContext/InputPriceFocusContext';
import { ModalContext } from 'src/contexts/ModalContext';
import {
  MultiSelectScope,
  SelectionMode,
  useMultiSelectionContext,
} from 'src/contexts/MultiSelectionContext';
import { useClearShiftSelectionOnSortingChange } from 'src/contexts/MultiSelectionContext/useClearShiftSelectionOnSortingChange';
import { useListingGroupSellerAccountSettings } from 'src/hooks/api/useListingGroupSellerAccountSettings';
import { useEnableSelectionByDefault } from 'src/hooks/useEnableSelectionByDefault';
import { useMatchMedia } from 'src/hooks/useMatchMedia';
import {
  useUserHasFeature,
  useUserHasTableTotalsFeature,
} from 'src/hooks/useUserHasFeature';
import { flattenListingGroup } from 'src/modals/GroupListings/components/groupingUtils';
import { GroupIdNameMap } from 'src/modals/MergeListings/utils';
import { getListingDetailsModalConfigWithDeepLink } from 'src/utils/inventoryUtils';
import { tryInvokeApi } from 'src/utils/tryExecuteUtils';
import {
  ActionOutboxEntityType,
  Event,
  Feature,
  GroupType,
  Listing,
  ListingClient,
  ListingDetailDataField,
  ListingGroup,
  SwapListingGroupInput,
} from 'src/WebApiController';

import { usePaginationSettings } from '../Table/hooks/usePaginationSettings';
import { RowWrapper } from '../Table/Table.types';
import { RowWrapperV2 } from '../TableV2/types';
import {
  ListingPricingForm,
  ListingPricingFormV2,
} from './configs/ListingPriceForm';
import { shouldHideInvisibleGroupData } from './configs/useShouldHideInvisibleGroupData';
import { PAGE_SIZE } from './ListingTable';
import { ListingWithEvent } from './ListingTable.types';

/**
 * Helper function to recursively flatten all sub-rows of a row into an array of IDs.
 * Used when selecting a parent row should also select all its children.
 */
const flattenSubRows = (
  row: Row<ListingWithEvent | null>,
  includeUnexpanded = true
): string[] => {
  const subRows = [row.id];
  const shouldInclude = includeUnexpanded || !row.getIsExpanded();
  if (row.subRows && row.subRows.length > 0 && shouldInclude) {
    return subRows.concat(
      row.subRows.flatMap((row) => flattenSubRows(row, includeUnexpanded))
    );
  }
  return subRows;
};

/**
 * Generate unique row ID for React Table - uses listing ID when available, falls back to index.
 */
export const getRowId = (originalRow: ListingWithEvent | null, index: number) =>
  (originalRow?.listing?.id ?? index).toString();

/**
 * Hook that manages table state including sorting, pagination, and row expansion.
 * Handles lifecycle events and state persistence for windowed/virtualized tables.
 */
export const useTableState = (
  event: Event,
  listings: Listing[],
  listingCount: number,
  data: (ListingWithEvent | null)[],
  disablePagination?: boolean,
  initState?: InitialTableState,
  onMount?: () => void,
  onUnmount?: (state: object) => void,
  keepActiveGroupsExpanded?: boolean
) => {
  const { data: listingGroupSettings } = useListingGroupSellerAccountSettings();
  const defaultCollapseListingGroups =
    listingGroupSettings?.defaultCollapseListingGroups ?? false;

  // Get sorting state from context - allows sharing sort state across multiple tables
  const { sorting, setSorting } = useActiveSortTableColumnContext();

  // Clear shift selections when sorting changes to avoid confusing UX
  useClearShiftSelectionOnSortingChange({ sortingState: sorting });

  // Track which groups are expanded/collapsed - restore from init state if available
  const [expandedState, setExpandedState] = useState<ExpandedState>(
    initState?.expanded ?? {}
  );

  // Determine current page based on the focused input price cell
  // This ensures pagination stays in sync when user is editing prices
  const { inputPriceFocusContext } = useInputPriceFocusContext();
  const currentPage = useMemo(() => {
    return inputPriceFocusContext &&
      inputPriceFocusContext.viagVirtId === event.virtId &&
      inputPriceFocusContext.rowIndex < listingCount
      ? inputPriceFocusContext.currentPageIndex
      : -1;
  }, [inputPriceFocusContext, event.virtId, listingCount]);

  // Set up pagination with the calculated current page
  const { pagination, setPagination } = usePaginationSettings(
    data.length,
    currentPage,
    PAGE_SIZE,
    disablePagination,
    initState
  );

  // Sync expanded state with external collapse/expand triggers
  useExpandCollapseListings({
    listings,
    expandedState,
    setExpandedState,
    keepActiveGroupsExpanded,
  });

  // Notify parent component when table mounts
  useEffect(() => {
    onMount?.();
  });

  // Notify parent component on unmount with current state for persistence
  // This is useful for virtualized tables that mount/unmount as user scrolls
  useEffect(() => {
    /**
     * Intent for `onMount` is use with 'windowing' in order to maintain user state
     * when the component is scrolled back into view.
     * Anything that needs to be persisted in `react-table` state should be added here.
     * Only update on unmount to ensure we aren't doing too many re-renders.
     */
    return () => onUnmount?.({ pagination, sorting });
  }, [pagination, sorting, onUnmount]);

  // Auto-expand all listing groups on first load for better UX
  // Skip auto-expansion if account setting defaultCollapseListingGroups is enabled
  useEffect(() => {
    // Only run if: no groups expanded yet AND there are groups to expand
    // AND collapse by default is NOT enabled
    if (
      Object.keys(expandedState).length === 0 &&
      listings?.some((l) => l.isLtGrp) &&
      !defaultCollapseListingGroups
    ) {
      setExpandedState(
        listings?.reduce(
          (r, l) => {
            if (l.isLtGrp) {
              // Expand the parent group
              r[l.id.toString()] = true;
              // Also expand any active sub-groups within this group
              (l as ListingGroup).groupItems.forEach((i) => {
                if ((i as ListingGroup).groupType === GroupType.Active) {
                  r[i.id.toString()] = true;
                }
              });
            }

            return r;
          },
          {} as Record<string, boolean>
        ) ?? {}
      );
    }
  }, [defaultCollapseListingGroups, expandedState, listings, setExpandedState]);

  return {
    sorting,
    setSorting,
    expandedState,
    setExpandedState,
    pagination,
    setPagination,
  };
};

/**
 * Hook that manages all row-level operations including selection, keyboard shortcuts,
 * row wrapping for pricing forms, and filtered row tracking.
 */
export const useRowOperations = (
  event: Event,
  data: (ListingWithEvent | null)[],
  selectedIds?: number[],
  defaultSelectionMode?: SelectionMode | null,
  isSideTable?: boolean,
  alwaysShowCheckbox?: boolean,
  onItemSelectionsChanged?: (_: number[]) => void,
  tableV2?: boolean
) => {
  const enableSelectionByDefault = useEnableSelectionByDefault();
  const hasTableRowSelectionV2Feature = useUserHasFeature(
    Feature.TableRowSelectionV2
  );

  // Set up keyboard shortcuts for row selection (shift+click, ctrl/cmd+click)
  const {
    lastSelectedRowRefIndex,
    allowShiftClickSelection,
    isShiftKeyPressed,
    isCtrlOrCmdKeyPressed,
    updateSelectionForRowAndSubRows,
  } = useSingleTableShortcutSelection<ListingWithEvent>({ isSideTable });

  // Get selection state from context - allows coordinating selection across multiple tables/views
  const {
    selectionMode: selectionModeOverride,
    setGroupItems,
    getSelection,
    getGroupToggleState,
    updateGroupFilteredIds,
    getItemToggleState,
  } = useMultiSelectionContext();

  // Check selection state for items in THIS table only
  const allSelected = useMemo(() => {
    const selectedCount = data.filter((item, index) => {
      const itemId = item?.listing?.id ? String(item.listing.id) : '';
      return getItemToggleState(itemId, event.virtId);
    }).length;

    return selectedCount === data.length && data.length > 0;
  }, [data, event.virtId, getItemToggleState]);
  useEffect(() => {
    if (tableV2) {
      // Toggle select all should clean up the listing selection
      onItemSelectionsChanged?.([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allSelected, tableV2]);

  // Use context selection mode if available, otherwise use prop-provided default
  const selectionMode = useMemo(
    () => selectionModeOverride ?? defaultSelectionMode,
    [defaultSelectionMode, selectionModeOverride]
  );
  // Clear selections when switching between single/multi/group selection modes
  useEffect(() => {
    onItemSelectionsChanged?.([]);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectionMode?.mode]);

  // Set up modal/navigation dependencies
  const isMobile = useMatchMedia('mobile');
  const { setActivePosEntity } = useActivePosEntityContext<Listing>();
  const { setModal } = useContext(ModalContext);
  const listingSelection = getSelection(event.virtId);
  const [visibleRowIds, setVisibleRowIds] = useState<number[]>();

  // Determine if checkboxes should be visible based on selection mode and configuration
  const showCheckbox = useMemo(
    () =>
      alwaysShowCheckbox ||
      selectionMode?.mode === MultiSelectScope.AllGroups ||
      listingSelection.isSingleGroupMultiSelect,
    [
      alwaysShowCheckbox,
      listingSelection.isSingleGroupMultiSelect,
      selectionMode?.mode,
    ]
  );

  // Build row selection state by flattening all listings and their groups
  // This converts the selected IDs into React Table's selection format
  const rowSelection = useMemo(() => {
    if (selectionMode == null) return {};

    // Flatten all listings including nested groups (up to 2 levels deep)
    return data
      ?.filter((d) => !d?.isCompListing) // Exclude comp listings from selection
      .flatMap((d) => [
        d?.listing,
        ...((d?.listing as ListingGroup)?.groupItems ?? []),
        ...((d?.listing as ListingGroup)?.groupItems ?? []).flatMap(
          (lg) => (lg as ListingGroup).groupItems ?? []
        ),
      ])
      ?.reduce<Record<string, boolean>>((result, listing) => {
        if (listing?.id) {
          // Check if entire group is selected without client-side filtering
          const isGroupSelectedWithoutClientSideFilter =
            getGroupToggleState(event.virtId).isGroupSelected &&
            !listingSelection.items.itemIds.length;

          // Mark as selected if either: group is selected, or this specific listing is selected
          result[listing.id] =
            isGroupSelectedWithoutClientSideFilter ||
            (listingSelection?.items.itemIds ?? selectedIds)?.includes(
              String(listing?.id)
            );
        }
        return result;
      }, {});
  }, [
    data,
    event.virtId,
    getGroupToggleState,
    listingSelection.items.itemIds,
    selectedIds,
    selectionMode,
  ]);

  // Handle filtered rows change - updates visible row tracking for client-side filtering
  const onFilteredRowsChange = useCallback(
    (filteredRows: Row<ListingWithEvent | null>[] | undefined) => {
      // Extract listing IDs from filtered rows (excluding groups and comps)
      const filteredItemIds = filteredRows
        ?.filter((r) => !r.original?.isLtGrp && !r.original?.isCompListing)
        .map((r) => r.original?.listing?.id)
        .filter((lid) => lid != null) as number[] | undefined;

      // Update context with filtered IDs for accurate selection state
      updateGroupFilteredIds(event.virtId, filteredItemIds);
      setVisibleRowIds(filteredItemIds);
    },
    [event.virtId, updateGroupFilteredIds]
  );

  const onRowSelectionChange = useCallback(
    (updater: Updater<RowSelectionState>) => {
      const newSelections =
        typeof updater === 'function' ? updater(rowSelection) : updater;

      // Guard: Prevent redundant updates when selection hasn't actually changed
      // This prevents unnecessary calls on initial render when React Table syncs state
      const hasChanged =
        Object.keys(newSelections).length !==
          Object.keys(rowSelection).length ||
        Object.keys(newSelections).some(
          (key) => newSelections[key] !== rowSelection[key]
        );
      if (!hasChanged) {
        return; // Early exit - no actual change
      }

      const newSelectedIds = Object.keys(newSelections).filter(
        (r) => newSelections[r] === true
      );

      if (selectionMode?.mode === 'singleItem' && !allowShiftClickSelection) {
        newSelectedIds.forEach((id) => {
          if (rowSelection[id] && newSelections[id]) {
            // if both of these are the same value and is true, we need to set it to false
            newSelections[id] = false;
          }
        });
      }

      setGroupItems(event.virtId, newSelections);
      if (hasTableRowSelectionV2Feature) {
        const newlySelectedIds = newSelectedIds
          .filter((id) => newSelections[id] && !rowSelection[id])
          .map((id) => parseInt(id));
        // Only update the newly selected
        onItemSelectionsChanged?.(newlySelectedIds);
      } else {
        onItemSelectionsChanged?.(
          newSelectedIds
            .filter((id) => newSelections[id])
            .map((id) => parseInt(id))
        );
      }
    },
    [
      rowSelection,
      selectionMode?.mode,
      allowShiftClickSelection,
      setGroupItems,
      event.virtId,
      hasTableRowSelectionV2Feature,
      onItemSelectionsChanged,
    ]
  );

  const onRowShiftSelect = useCallback(
    (
      tableRows: Row<ListingWithEvent | null>[] | undefined,
      row: Row<ListingWithEvent | null>,
      index: number
    ) => {
      if (!tableRows || !allowShiftClickSelection || showCheckbox) {
        return;
      }

      const rowId = row.id.toString();
      const newIsSelected = !rowSelection[rowId];

      if (isShiftKeyPressed && lastSelectedRowRefIndex.current !== null) {
        const start = Math.min(lastSelectedRowRefIndex.current, index);
        const end = Math.max(lastSelectedRowRefIndex.current, index);
        const newRowSelection = { ...rowSelection };

        for (let i = start; i <= end; i++) {
          const row = tableRows[i];
          if (row) {
            updateSelectionForRowAndSubRows({
              row,
              rowId: row.id.toString(),
              newIsSelected,
              newRowSelection,
            });
          }
        }

        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else if (isCtrlOrCmdKeyPressed) {
        const newRowSelection = { ...rowSelection };
        newRowSelection[rowId] = newIsSelected;
        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected,
          newRowSelection,
        });
        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      } else {
        // No modifier key pressed, reset selection
        const newRowSelection = {};

        updateSelectionForRowAndSubRows({
          row,
          rowId,
          newIsSelected,
          newRowSelection,
        });

        lastSelectedRowRefIndex.current = index;
        onRowSelectionChange(newRowSelection);
      }
    },
    [
      allowShiftClickSelection,
      isCtrlOrCmdKeyPressed,
      isShiftKeyPressed,
      lastSelectedRowRefIndex,
      onRowSelectionChange,
      rowSelection,
      showCheckbox,
      updateSelectionForRowAndSubRows,
    ]
  );

  const onRowShiftSelectV2 = useCallback(
    (
      tableRows: Row<ListingWithEvent | null>[] | undefined,
      row: Row<ListingWithEvent | null>,
      index: number
    ) => {
      // Not in shift select
      if (!isShiftKeyPressed || lastSelectedRowRefIndex.current == null) {
        const flattenedRowIds = flattenSubRows(row);
        if (
          selectedIds &&
          selectedIds.length > 0 &&
          selectedIds?.every((id) => flattenedRowIds.includes(id.toString()))
        ) {
          onItemSelectionsChanged?.([]);
        } else {
          onItemSelectionsChanged?.(flattenedRowIds.map((id) => parseInt(id)));
        }
        return;
      }

      const start = Math.min(lastSelectedRowRefIndex.current, index);
      const end = Math.max(lastSelectedRowRefIndex.current, index);
      const rangedRows = tableRows?.slice(start, end + 1) ?? [];
      const rangedRowIds = rangedRows
        .flatMap((row) => flattenSubRows(row, false))
        .map((id) => parseInt(id));

      if (
        rangedRowIds.length > 0 &&
        selectedIds &&
        selectedIds.length > 0 &&
        rangedRowIds.every((id) => selectedIds.includes(id))
      ) {
        // All ids are selected, unselect the ids
        onItemSelectionsChanged?.(
          selectedIds.filter((id) => !rangedRowIds.includes(id))
        );
      } else if (rangedRowIds.length > 0) {
        onItemSelectionsChanged?.(
          Array.from(new Set(rangedRowIds.concat(selectedIds ?? [])))
        );
      }
    },
    [
      isShiftKeyPressed,
      lastSelectedRowRefIndex,
      onItemSelectionsChanged,
      selectedIds,
    ]
  );

  const onRowSelect = useCallback(
    (
      tableRows: Row<ListingWithEvent | null>[] | undefined,
      row: Row<ListingWithEvent | null>,
      index: number
    ) => {
      if (hasTableRowSelectionV2Feature || enableSelectionByDefault) {
        onRowShiftSelectV2(tableRows, row, index);
        lastSelectedRowRefIndex.current = index;
        return;
      }

      row.toggleSelected();
      onRowShiftSelect?.(tableRows, row, index);
    },
    [
      enableSelectionByDefault,
      hasTableRowSelectionV2Feature,
      lastSelectedRowRefIndex,
      onRowShiftSelect,
      onRowShiftSelectV2,
    ]
  );

  const onRowHotkeySelect = useCallback(
    (row: Row<ListingWithEvent | null>) => {
      const listing = row.original?.listing;
      if (listing) {
        setActivePosEntity(listing.id, listing.idOnMkp, true, [
          ListingDetailDataField.Basic,
        ]);
        setModal(
          getListingDetailsModalConfigWithDeepLink(isMobile, listing.id, true)
        );
      }
    },
    [isMobile, setActivePosEntity, setModal]
  );

  return {
    selectionMode,
    showCheckbox,
    visibleRowIds,
    rowSelection,
    listingSelection,
    isShiftKeyPressed,
    onRowSelect,
    onRowHotkeySelect,
    onRowShiftSelect,
    onFilteredRowsChange,
    onRowSelectionChange,
  };
};

/**
 * Hook that manages drag and drop operations for listing rows.
 * Enables moving listings between groups and reordering within groups.
 */
export const useRowDragAndDrop = (
  listings: Listing[],
  showCheckbox: boolean,
  rowSelection: Record<string, boolean>,
  selectedListingIds: number[] | undefined,
  setExpandedState: React.Dispatch<React.SetStateAction<ExpandedState>>,
  onRowSelectionChange: (updater: Updater<RowSelectionState>) => void
) => {
  const { activeAccountWebClientConfig } = useAppContext();
  const { showErrorDialog } = useClientLogContext();
  const { updateExpandedListItems } = useCatalogDataContext();

  // Check if user has permission to reorder listings within a group
  const hasReorderListingInGroupFeature = useUserHasFeature(
    Feature.ReorderListingInGroup
  );

  // Track which listing is currently being dragged
  const [draggingListing, setDraggingListing] = useState<Listing>();

  // Determine which listings to drop - if dragging a selected listing, drop all selected
  const listingsToDrop = useMemo((): Listing[] => {
    if (!draggingListing) {
      return [];
    }

    // Get all currently selected listing IDs
    const selectedIds = showCheckbox
      ? Object.entries(rowSelection)
          .filter(([_, value]) => value)
          .map(([key, _]) => key)
          .map(Number)
      : (selectedListingIds ?? []);

    // If the dragged listing is part of a selection, drop all selected listings
    if (!isEmpty(selectedIds)) {
      if (selectedIds.includes(draggingListing.id)) {
        const selectedListings = (listings
          ?.flatMap(flattenListingGroup)
          .filter(({ id, isLtGrp }) => !isLtGrp && selectedIds.includes(id)) ??
          []) as Listing[];
        return selectedListings;
      }
    }

    // Otherwise just drop the single dragged listing
    return [draggingListing];
  }, [
    draggingListing,
    listings,
    rowSelection,
    selectedListingIds,
    showCheckbox,
  ]);

  // Handler for when drag starts - store the listing being dragged
  const onDragListingStart = useCallback(
    (event: DragStartEvent) => {
      const activeListing = listings
        ?.flatMap(flattenListingGroup)
        .find(({ id }) => id === event.active?.data?.current?.listingId);
      if (activeListing) {
        setDraggingListing(activeListing as Listing);
      }
    },
    [listings]
  );

  // Handler for when drag ends - move listings to new group or reorder within group
  const onDragListingEnd = useCallback(
    async (e: DragEndEvent) => {
      const { over, active } = e;

      // Extract drag source and destination information
      const fromListingGroupId = active?.data?.current
        ?.currentListingGroupId as string | null | undefined;
      const toListingGroupId = over?.data?.current?.ltGrpId as
        | string
        | null
        | undefined;
      const activeListingId = active?.data?.current?.listingId as
        | number
        | null
        | undefined;

      // Helper to expand target group and select dropped listing after drop completes
      const onDropCompleted = () => {
        const targetListingGroup = listings?.find(
          (l) => l.isLtGrp && l.ltGrpId === toListingGroupId
        );
        if (!targetListingGroup) {
          return;
        }

        // Prepare expansion state for all items in the target group
        const allGroups = (targetListingGroup as ListingGroup).groupItems
          .map((item) => item.id)
          .reduce(
            (res, id) => {
              res[id.toString()] = true;
              return res;
            },
            {} as Record<string, boolean>
          );

        // Expand the target group and all its sub-groups
        setExpandedState((prev) => {
          if (typeof prev === 'boolean') {
            return prev;
          }
          return {
            ...prev,
            ...allGroups,
            [targetListingGroup.id]: true,
          };
        });

        // Select the dropped listing for visual feedback
        if (activeListingId) {
          onRowSelectionChange({
            [activeListingId + '']: true,
          });
        }
      };

      // Case 1: Reorder within the same group (if feature is enabled)
      if (
        hasReorderListingInGroupFeature &&
        fromListingGroupId != null &&
        fromListingGroupId === toListingGroupId
      ) {
        const fromPriority = active?.data?.current?.listingGroupPriority as
          | number
          | null
          | undefined;
        const toPriority = over?.data?.current?.listingGroupPriority as
          | number
          | null
          | undefined;

        // Only reorder if dropping at a different position
        if (
          activeListingId &&
          fromPriority &&
          toPriority &&
          fromPriority != toPriority
        ) {
          setDraggingListing(undefined);

          // Call API to update listing order within the group
          return await tryInvokeApi(
            async () => {
              const updatedListings = await new ListingClient(
                activeAccountWebClientConfig
              ).reorderListingInGroup({
                listingId: activeListingId,
                fromPosition: fromPriority,
                toPosition: toPriority,
                listingGroupId: fromListingGroupId,
              });
              updateExpandedListItems(updatedListings);
              onDropCompleted();
            },
            (error) => {
              showErrorDialog(
                'ListingGroupClient.reorderListingInGroup',
                error,
                {
                  trackErrorData: {
                    listingGroupId: fromListingGroupId,
                    listingId: activeListingId,
                  },
                }
              );
            },
            () => {
              setDraggingListing(undefined);
            }
          );
        }
      }

      // Case 2: Move listings to a different group
      if (!toListingGroupId) {
        return; // No valid drop target
      }

      // Filter out listings that are already in the target group (no-op)
      const sanitizedListings = listingsToDrop.filter(
        (l) => l.ltGrpId !== toListingGroupId
      );

      if (isEmpty(sanitizedListings)) {
        return; // Nothing to move
      }

      setDraggingListing(undefined);

      // Call API to batch move listings to the new group
      return await tryInvokeApi(
        async () => {
          const input: SwapListingGroupInput[] = sanitizedListings.map((l) => ({
            listingId: l.id,
            listingGroupId: l.ltGrpId,
            quantity: l.availQty,
            adminHoldExpirationDate: l.adminHoldExpiredOn,
          }));
          const updatedListings = await new ListingClient(
            activeAccountWebClientConfig
          ).batchSwapListingGroup({
            listings: input,
            toListingGroupId,
          });
          updateExpandedListItems(updatedListings);
          onDropCompleted();
        },
        (error) => {
          showErrorDialog('ListingGroupClient.batchSwapListingGroup', error, {
            trackErrorData: {
              toListingGroupId,
            },
          });
        },
        () => {
          setDraggingListing(undefined);
        }
      );
    },
    [
      activeAccountWebClientConfig,
      hasReorderListingInGroupFeature,
      listings,
      listingsToDrop,
      onRowSelectionChange,
      setExpandedState,
      showErrorDialog,
      updateExpandedListItems,
    ]
  );

  // Configure drag sensor to require 5px movement before drag starts
  // This prevents accidental drags when clicking on rows
  const pointerSensor = useSensor(PointerSensor, {
    activationConstraint: { distance: 5 },
  });
  const sensors = useSensors(pointerSensor);

  return {
    sensors,
    listingsToDrop,
    onDragListingStart,
    onDragListingEnd,
  };
};

/**
 * Hook that transforms raw listing data into the format required by the table.
 * Handles data mapping, filtering, and merging of regular and comp listings.
 */
export const useTableData = (
  event: Event,
  listings: Listing[],
  compListings?: Listing[],
  listingGroupMap?: GroupIdNameMap,
  hideUnselectableListings?: boolean,
  showUnselectableListingsOnly?: boolean,
  getListingNotEligibleForSelection?: (
    listings: Listing[] | null
  ) => { listing: Listing; ineligibleReason: string | undefined }[] | undefined,
  getListingTicketClassColor?: (listing: Listing) => string | undefined
) => {
  const hasTableTotalsFeature = useUserHasTableTotalsFeature(
    ActionOutboxEntityType.Listing
  );

  // Step 1: Determine which listings can/cannot be selected
  // Split data transformation into smaller memoized chunks for better performance
  const listingsWithIneligibleReasons = useMemo(
    () =>
      getListingNotEligibleForSelection?.(listings) ??
      listings?.map((l) => ({
        listing: l,
        ineligibleReason: undefined,
      })),
    [getListingNotEligibleForSelection, listings]
  );

  // Step 2: Create mapper function that adds event context and UI metadata to each listing
  const mapListingWithEvent = useCallback(
    (l: { listing: Listing; ineligibleReason: string | undefined }) =>
      ({
        event,
        listing: l.listing,
        selectionDisabledReason: l.ineligibleReason,
        ticketClassColor: getListingTicketClassColor?.(l.listing),
        isLtGrp: l.listing.isLtGrp,
        ltGroupName:
          listingGroupMap && l.listing.ltGrpId
            ? listingGroupMap[l.listing.ltGrpId]
            : undefined,
        shouldHideNumerColumn: shouldHideInvisibleGroupData(
          hasTableTotalsFeature,
          l.listing
        ),
      }) as ListingWithEvent,
    [event, getListingTicketClassColor, hasTableTotalsFeature, listingGroupMap]
  );

  // Step 3: Map regular listings with event context
  const mappedListings = useMemo<ListingWithEvent[] | undefined>(
    () => listingsWithIneligibleReasons?.map(mapListingWithEvent),
    [listingsWithIneligibleReasons, mapListingWithEvent]
  );

  // Step 4: Map comp listings separately (they have different properties)
  const mappedCompListings = useMemo<ListingWithEvent[] | undefined>(
    () =>
      compListings?.map((l) => ({
        event,
        listing: l,
        selectionDisabledReason: undefined,
        isCompListing: true, // Mark as comp for special handling
        ticketClassColor: getListingTicketClassColor?.(l),
      })),
    [compListings, event, getListingTicketClassColor]
  );

  // Step 5: Combine regular and comp listings into one array
  const allMappedListings = useMemo(() => {
    if (!mappedListings) return mappedCompListings;
    if (!mappedCompListings) return mappedListings;
    return [...mappedListings, ...mappedCompListings];
  }, [mappedListings, mappedCompListings]);

  // Step 6: Apply final filtering based on selection eligibility
  const filteredListings = useMemo(
    () =>
      allMappedListings?.filter(
        (l) =>
          showUnselectableListingsOnly
            ? Boolean(l.selectionDisabledReason) // Only show unselectable
            : hideUnselectableListings
              ? !l.selectionDisabledReason // Hide unselectable
              : true // Show all
      ),
    [allMappedListings, showUnselectableListingsOnly, hideUnselectableListings]
  );

  return {
    filteredListings,
    mapListingWithEvent, // Export for creating aggregated listings
  };
};

export const useTableRowWrapper = (
  event: Event,
  pagination: PaginationState,
  displayColumns:
    | ColumnDef<ListingWithEvent | null>[]
    | ColumnDef<ListingWithEvent>[]
) => {
  // ListingTable doesn't always have these context wrappers - remember not to assume these are NOT NULL
  const { setDisabledFocusContexts, disabledFocusContexts } =
    useInputPriceFocusContext();

  const onRowWrapper: RowWrapper<ListingWithEvent | null> = useCallback(
    (rowData, tableRow, rowIndex) => {
      if (rowData?.isCompListing) {
        // If it's comp listing, put into `disabledFocusContexts`
        // We don't want to price on that
        if (
          !disabledFocusContexts?.find(
            (p) =>
              p?.currentPageIndex === pagination.pageIndex &&
              p?.rowIndex === rowIndex &&
              p?.viagVirtId === event.virtId
          )
        ) {
          setDisabledFocusContexts((prev: FocusContext[] | undefined) => {
            return [
              ...(prev ?? []),
              {
                viagVirtId: event.virtId,
                rowIndex,
                // can be either, we don't use it when checking for focus
                priceField: 'listPrice',
                currentPageIndex: pagination.pageIndex,
              },
            ];
          });
        }
      } else {
        if (
          disabledFocusContexts?.find(
            (p) =>
              p?.currentPageIndex === pagination.pageIndex &&
              p?.rowIndex === rowIndex &&
              p?.viagVirtId === event.virtId
          )
        ) {
          setDisabledFocusContexts((prev: FocusContext[] | undefined) => {
            return prev?.filter(
              (p) =>
                p?.currentPageIndex !== pagination.pageIndex ||
                p?.rowIndex !== rowIndex ||
                p?.viagVirtId !== event.virtId
            );
          });
        }
      }

      return (
        <ListingPricingForm
          key={rowData?.listing?.id ?? rowIndex}
          rowData={
            rowData && {
              ...rowData,
              rowIndex,
              currentPageIndex: pagination.pageIndex,
            }
          }
          tableRow={tableRow}
        />
      );
    },
    [
      disabledFocusContexts,
      event.virtId,
      pagination.pageIndex,
      setDisabledFocusContexts,
    ]
  );

  const onRowWrapperV2: RowWrapperV2<ListingWithEvent> = useCallback(
    (row, rowIndex, getRowContent) => {
      const rowData = row.original;
      if (rowData?.isCompListing) {
        // If it's comp listing, put into `disabledFocusContexts`
        // We don't want to price on that
        if (
          !disabledFocusContexts?.find(
            (p) =>
              p?.currentPageIndex === pagination.pageIndex &&
              p?.rowIndex === rowIndex &&
              p?.viagVirtId === event.virtId
          )
        ) {
          setDisabledFocusContexts((prev: FocusContext[] | undefined) => {
            return [
              ...(prev ?? []),
              {
                viagVirtId: event.virtId,
                rowIndex,
                // can be either, we don't use it when checking for focus
                priceField: 'listPrice',
                currentPageIndex: pagination.pageIndex,
              },
            ];
          });
        }
      } else {
        if (
          disabledFocusContexts?.find(
            (p) =>
              p?.currentPageIndex === pagination.pageIndex &&
              p?.rowIndex === rowIndex &&
              p?.viagVirtId === event.virtId
          )
        ) {
          setDisabledFocusContexts((prev: FocusContext[] | undefined) => {
            return prev?.filter(
              (p) =>
                p?.currentPageIndex !== pagination.pageIndex ||
                p?.rowIndex !== rowIndex ||
                p?.viagVirtId !== event.virtId
            );
          });
        }
      }

      return (
        <ListingPricingFormV2
          columns={displayColumns.map((column) => column.id).join(',')}
          key={rowData?.listing?.id ?? rowIndex}
          row={row}
          rowIndex={rowIndex}
          getRowContent={getRowContent}
        />
      );
    },
    [
      disabledFocusContexts,
      event.virtId,
      pagination.pageIndex,
      setDisabledFocusContexts,
      displayColumns,
    ]
  );

  return {
    onRowWrapper,
    onRowWrapperV2,
  };
};

/**
 * Function to adapt TanStack table rows into grouped structure for ListingTableV2.
 * This includes ListingTable-specific business logic for handling Active listing groups.
 * Skips Active listing groups that are direct children (depth 1) of top-level parent.
 */
export function processListingGroupedRows<T>(
  tableRows: Row<T>[],
  isGroupRow?: (data: T) => boolean
): Row<T>[] {
  const ungroupedRows: typeof tableRows = [];
  const groupsMap = new Map<string, { parent: Row<T>; children: Row<T>[] }>();

  // Build row lookup map for O(1) access instead of O(n) find
  const rowsById = new Map<string, Row<T>>();
  tableRows.forEach((row) => {
    rowsById.set(row.id, row);
  });

  // Process rows
  tableRows.forEach((row) => {
    if (row.depth === 0) {
      if (isGroupRow?.(row.original)) {
        // Top-level parent group
        groupsMap.set(row.id, { parent: row, children: [] });
      } else {
        // Ungrouped row
        ungroupedRows.push(row);
      }
    } else if (row.depth > 0 && row.parentId) {
      // Child row - walk up to find top-level parent
      let current = row;
      let topLevelParentId: string | null = null;

      while (current.depth > 0 && current.parentId) {
        const parent = rowsById.get(current.parentId);
        if (!parent) break;

        if (parent.depth === 0) {
          topLevelParentId = parent.id;
          break;
        }
        current = parent;
      }

      // Add to parent's children if found
      if (topLevelParentId) {
        // Skip Active listing groups that are direct children (depth 1) of top-level parent
        const listing = (row.original as any)?.listing;
        const isActiveGroupAtDepth1 =
          row.depth === 1 &&
          listing?.isLtGrp &&
          listing?.groupType === GroupType.Active;

        if (!isActiveGroupAtDepth1) {
          groupsMap.get(topLevelParentId)?.children.push(row);
        }
      }
    }
  });
  const adaptedRows = [...ungroupedRows];

  Array.from(groupsMap.values()).forEach(
    (group: { parent: Row<T>; children: Row<T>[] }) => {
      adaptedRows.push(group.parent); // Add parent first
      adaptedRows.push(...group.children);
    }
  );

  return adaptedRows;
}
